import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiGetUserStrategies } from "api/apiStrategies";
import { apiGetStrategyTabs } from "api/apiStrategyTabs";
import { useSelector } from "react-redux";

import StrategiesTable from "./StrategiesTable";
import StrategyTabsBar from "./StrategyTabsBar";
import StrategiesOverview from "./StrategiesOverview";
import LoadedStrategy from "./LoadedStrategy";
import { Button, Tabs } from "antd";
import { useTradingRefresh } from "components/Trading/tradingRefresh";

const defaultTab = {
  label: `All Strategies`,
  key: "all",
};

const Strategies = () => {
  const accountType = useSelector((state) => state.main.accountType);
  // children call getUserStrategies after a change: refreshes this list
  // (effect below) and the rest of the Trading page
  const { refreshKey, notifyChanged } = useTradingRefresh();
  // ?strategyId= (links, pending actions) loads that strategy and scrolls to it
  const [searchParams, setSearchParams] = useSearchParams();
  const strategyFromUrl =
    searchParams.get("instrument") !== "options" &&
    searchParams.get("strategyId");
  const loadedRef = useRef(null);

  const [strategyTabs, setStrategyTabs] = useState([defaultTab]);
  const [selectedStrategyTab, setSelectedStrategyTab] = useState("all");
  const [strategies, setStrategies] = useState([]);
  const [showEnabledStrategies, setShowEnabledStrategies] = useState(true);
  const [loadedStrategies, setLoadedStrategies] = useState([]);
  const [loadedStrategySelectedTab, setLoadedStrategySelectedTab] =
    useState("");

  const selectedIds = useMemo(
    () =>
      loadedStrategies.reduce((acc, item) => {
        acc[item._id] = 1;
        return acc;
      }, {}),
    [loadedStrategies]
  );

  const getUserStrategyTabs = async (selectTab) => {
    try {
      const response = await apiGetStrategyTabs();
      const tabs = [defaultTab];
      response.forEach((item) => {
        tabs.push({
          label: item.name,
          key: item._id,
          description: item.description,
          children: (
            <p
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                marginBlockEnd: 10,
              }}
              dangerouslySetInnerHTML={{ __html: item.description }}
            />
          ),
        });
      });
      setStrategyTabs(tabs);
      if (selectTab) {
        setSelectedStrategyTab(selectTab);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const getUserStrategies = async () => {
    const formData = {
      enabled: showEnabledStrategies,
      accountType,
    };

    if (selectedStrategyTab !== "all") {
      formData.strategyTabs = selectedStrategyTab;
    }

    const response = await apiGetUserStrategies(formData);
    setStrategies(response);

    // fresh data for the loaded ones; ones outside the current filter stay
    setLoadedStrategies((prevState) =>
      prevState.map(
        (loaded) => response.find((item) => item._id === loaded._id) || loaded,
      ),
    );
  };

  const openFromUrl = async (strategyId) => {
    const next = new URLSearchParams(searchParams);
    next.delete("strategyId");
    setSearchParams(next, { replace: true });

    // may be outside the current group/enabled filter
    const strategy =
      strategies.find((item) => item._id === strategyId) ||
      (await apiGetUserStrategies({ accountType })).find(
        (item) => item._id === strategyId,
      );
    if (strategy) {
      onLoadStrategy(strategy);
      setTimeout(() =>
        loadedRef.current?.scrollIntoView({ behavior: "smooth" }),
      );
    }
  };

  const onLoadStrategy = (strategy) => {
    setLoadedStrategies((prevState) => {
      const isLoaded = prevState.find((item) => item._id === strategy._id);
      if (isLoaded) {
        return prevState;
      }
      return [strategy, ...prevState];
    });
    setLoadedStrategySelectedTab(strategy._id);
  };

  const handleChangeTab = (resultId) => {
    setLoadedStrategySelectedTab(resultId);
  };

  const onUnloadStrategy = (strategyId) => {
    setLoadedStrategies((prevState) => {
      const updatedState = prevState.filter((item) => item._id !== strategyId);
      if (strategyId === loadedStrategySelectedTab) {
        setLoadedStrategySelectedTab(updatedState[0]?._id);
      }

      return updatedState;
    });
  };

  const clearLoadedStrategies = () => {
    setLoadedStrategies([]);
  };

  const onEdit = (targetKey, action) => {
    if (action === "add") {
      // add();
    } else {
      onUnloadStrategy(targetKey);
    }
  };

  const items = useMemo(() => {
    return loadedStrategies.map((item) => {
      return {
        key: item._id,
        label: `${item.symbol} - ${item.name}`,
        children: (
          <LoadedStrategy
            selectedStrategy={item}
            getUserStrategies={notifyChanged}
            strategyTabs={strategyTabs}
          />
        ),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedStrategies, strategyTabs]);

  useEffect(() => {
    getUserStrategies();
  }, [showEnabledStrategies, accountType, selectedStrategyTab, refreshKey]);

  useEffect(() => {
    getUserStrategyTabs();
  }, []);

  useEffect(() => {
    if (strategyFromUrl) openFromUrl(strategyFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategyFromUrl]);

  return (
    <>
      <StrategyTabsBar
        strategyTabs={strategyTabs}
        getUserStrategyTabs={getUserStrategyTabs}
        selectedStrategyTab={selectedStrategyTab}
        setSelectedStrategyTab={setSelectedStrategyTab}
        showEnabledStrategies={showEnabledStrategies}
        setShowEnabledStrategies={setShowEnabledStrategies}
      />

      <StrategiesTable
        strategies={strategies}
        getUserStrategies={notifyChanged}
        onLoadStrategy={onLoadStrategy}
        onUnloadStrategy={onUnloadStrategy}
        selectedIds={selectedIds}
        summary={<StrategiesOverview strategies={strategies} />}
      />

      {loadedStrategies.length > 0 && (
        <div ref={loadedRef} style={{ minHeight: 800 }}>
          <Tabs
            type="editable-card"
            items={items}
            onEdit={onEdit}
            tabBarExtraContent={{
              right: (
                <Button type="primary" onClick={clearLoadedStrategies}>
                  Clear
                </Button>
              ),
            }}
            hideAdd
            activeKey={loadedStrategySelectedTab}
            onChange={handleChangeTab}
          />
        </div>
      )}
    </>
  );
};

export default Strategies;
